import http from "./http.service";
import Promisable from "./promisable.service";
import { modalActions } from "redux/slices/modal";
import { BookingType } from "redux/slices/booking";
import { NavigateFunction } from "react-router-dom";
import { getAppDispatch } from "utils/dispatch.util";
import { bookingActions } from "redux/slices/booking";
import { formLoaderActions } from "redux/slices/formLoader";
import ObjectService from "utils/object.util";
import DateService from "utils/date.util";
import { mapAction } from "redux/slices/map/mapSlice";
import ToasterService from "utils/toaster.util";
import { PriceService } from "utils/price_calculation";

const url = "/trip";

const BookingService = {
  addBooking: async (data: any, navigate?: NavigateFunction) => {
    const dispatch = getAppDispatch();
    dispatch?.(bookingActions.setLoader(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.newBooking({ type: "admin", booking: trip }));
      navigate?.(`/booking-details/${trip._id}`);
      dispatch?.(mapAction.setStartFormatted([]));
      dispatch?.(mapAction.setEndFormatted([]));
    }
    dispatch?.(bookingActions.setLoader(false));


    return [success, error];
  },

  addDuplicateBooking: async (data: any, navigate?: NavigateFunction) => {

    http.setJWT();
    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/duplicate`, data)
    );

    if (success) {
      navigate?.(`/bookings?tab=1`);
    }

    return [success, error];
  },

  addComplimentaryBooking: async (data: any, navigate?: NavigateFunction) => {
    const dispatch = getAppDispatch();
    dispatch?.(bookingActions.setLoader(true));

    http.setJWT();
    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/add-complementary`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.newBooking({ type: "admin", booking: trip }));
      navigate?.(`/booking-details/${trip._id}`);
      dispatch?.(mapAction.setStartFormatted([]));
      dispatch?.(mapAction.setEndFormatted([]));
    }

    dispatch?.(bookingActions.setLoader(false));
    return [success, error];
  },

  markAsComplementary: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();
    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/mark-complementary/${id}`)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
      dispatch?.(modalActions.closeModal());
      ToasterService.showSuccess("Booking marked as complimentary");
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  updateBooking: async (id: string, data: any, navigate?: NavigateFunction) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/update/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
      navigate?.(`/booking-details/${id}`);
      dispatch?.(mapAction.setStartFormatted([]));
      dispatch?.(mapAction.setEndFormatted([]));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  getBooking: async (id: string, isUpdate: boolean = false) => {
    const dispatch = getAppDispatch();
    dispatch?.(bookingActions.setLoader(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/${id}`)
    );

    if (success) {
      const { trip } = success.data.data;
      const { message } = success.data;
      if (message !== "") {
        if (message.includes("rejected")) {
          ToasterService.showError(message);
        } else {
          ToasterService.showSuccess(message);
        }
      }
      dispatch?.(bookingActions.setBooking(trip));
    } else dispatch?.(bookingActions.setBooking({ data: "Not Found" }));
    if (!success || !success?.data?.data?.trip) {
      dispatch?.(bookingActions.setLoader(false));
    } else if (!isUpdate) {
      dispatch?.(bookingActions.setLoader(false));
    }
    return [success, error];
  },

  retryAggregatorSync: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(bookingActions.setLoader(true));
    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/retry-aggregator-sync/${id}`)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
      ToasterService.showSuccess(
        success.data.message || "External booking sync retried"
      );
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(bookingActions.setLoader(false));
    return [success, error];
  },

  getMcPrices: async (data: any) => {
    http.setJWT();
    return Promisable.asPromise(http.post(`${url}/mc-prices`, data));
  },

  sendToMc: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(bookingActions.setLoader(true));
    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/send-to-mc/${id}`)
    );

    if (success) {
      ToasterService.showSuccess(
        success.data.message || "Booking sent to MC"
      );
      await BookingService.getBooking(id);
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(bookingActions.setLoader(false));
    return [success, error];
  },

  getBookings: async (type: BookingType, data: any, query: string) => {
    const dispatch = getAppDispatch();

    if (data?.all) dispatch?.(modalActions.setLoading(true));
    else dispatch?.(bookingActions.setLoading({ type, loading: true }));

    http.setJWT();
    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/${type}${query}`, data)
    );

    if (success) {
      const { trips, list, count, bcount, source } = success.data.data;

      if (data?.all)
        dispatch?.(
          modalActions.updateData({
            table_data: trips.map((booking: any) => ({
              "Booking ID": booking.booking_number,
              "Added by ( Admin/Company/Customer)": booking.added_by,
              'Source (website/app)': booking?.source_type || '--',
              "Created date": DateService.getFormattedDateTimeForExcel(
                booking.createdAt
              ),
              "Created Time": DateService.getTimeString(
                booking.createdAt
              ),
              Pickup: booking.pickup.address,
              "Destination/Hours": booking.destination.address
                ? booking.destination.address
                : booking.duration,
              "Booking Date": DateService.getFormattedDateTimeForExcel(booking.date),
              Time: DateService.getTimeString(booking.date),
              "Trip Type": (booking?.trip_type === 'trip' || booking?.trip_type === 'city_trip' || booking?.booking_type === 'transfer') ?
                (booking?.distance_in_km > 100 || Number(booking?.distance) > 100) ? "City Trip" : "trip" : "hourly",
              Class: booking.class_name,
              "Start time": DateService.getFormattedDateTime(
                booking.ride_start_time
              ),
              "End time": DateService.getFormattedDateTime(
                booking.ride_end_time
              ),
              "Payment Status": booking.payment_status,
              "Booking Status ": booking.current_status,
              "Net amount before discount":
                booking.discount_info?.discount_value > 0
                  ? parseFloat(booking?.price_details?.netto_price || 0) +
                  parseFloat(booking?.price_details?.admin_discount || 0)
                  : parseFloat(booking?.price_details?.netto_price || 0),
              "Gross amount before discount": parseFloat(booking?.price_details?.total_price || 0),
              "Discount percentage":
                booking.discount_info?.discount_type === "percentage"
                  ? booking.discount_info?.discount_value
                  : 0,
              "Discounted amount": booking.price_details.coupon.value > 0 ?
                booking.price_details.coupon.value :
                booking.discount_info?.discount_type === "percentage"
                  ? booking.price_details.admin_discount
                  : parseFloat(booking.discount_info?.discount_value),
              "Coupon": booking?.price_details?.coupon?.code || "",
              "Net amount after discount ": parseFloat(PriceService.get_netto_from_brutto(booking.price_details, 15).toFixed(2)),
              "Tax Amount 15%": parseFloat(PriceService.brutto_exclude_tax(booking.price_details.brutto_price, 15).toFixed(2)),
              "Gross amount after discount ":
                parseFloat(booking.price_details.brutto_price),
              "Amount paid":
                booking.payment_status === "paid"
                  ? parseFloat(booking.price_details.brutto_price)
                  : "",
              "Refund amount ": parseFloat(booking.price_details.refund.reduce((acc: any, obj: any) => acc + obj.amount, 0)?.toFixed(2)),
              "Company Name": booking.company_details?.company_name,
              "First name": booking.booking_for_someone
                ? booking.booking_for_details.first_name
                : booking.user_details.first_name,
              "Last name": booking.booking_for_someone
                ? booking.booking_for_details.last_name
                : booking.user_details.last_name,
              Phone: booking.booking_for_someone
                ? booking.booking_for_details?.phone
                : booking.user_details?.phone,
              "Passport/ID": booking?.passport,
              "Notes from admin":
                booking.added_by === "admin"
                  ? booking.additional_info.notes
                  : "",
              "Private notes from admin": booking?.private_notes?.message,
              "Notes from customer/company":
                booking.added_by !== "admin"
                  ? booking.additional_info.notes
                  : "",
              "Performed by ( Partner name )":
                booking.partner_details?.company_name,
              "Driver Name": `${ObjectService.getFullName(
                booking.driver_details
              )}`,
              "Driver Phone": booking.driver_details?.phone,
              Plate: booking.vehicle_details?.license_plate,
              City: booking?.city_name,
              "Payment Reference /Payfort ID": booking.payment_intent,
              "Payment Reference /Stripe ID": booking?.stripe_payment_detail?.payment_intent || "",
              "Free Cancellation": booking.current_status === "cancelled" ? booking.isFreeCancel ? "Free" : "Chargable" : "",
              "Cancelled By": booking.current_status === "cancelled" ? booking.cancelled_by : "",
              "Cancellation Reason": booking.current_status === "cancelled" ? booking.cancelation_reason : "",
              "Cancellation Date": booking.current_status === "cancelled" ? DateService.getFormattedDateTime(booking.cancelation_date_time) : "",
              "Cancellation Time": booking.current_status === "cancelled" ? DateService.getTimeString(booking.cancelation_date_time) : "",
            })),
          })
        );
      else {
        dispatch?.(bookingActions.setBookings({ type, count, bookings: trips || list, bcount, source: source || "" }));

      }
    }

    if (data?.all) dispatch?.(modalActions.setLoading(false));
    else {
      setTimeout(() => {
        dispatch?.(bookingActions.setLoading({ type, loading: false }));
      }, 0);
    }

    return [success, error];
  },

  getBookingsReveune: async (type: BookingType, data: any) => {
    const dispatch = getAppDispatch();

    // Do not toggle table loading — this races with getBookings and flashes an empty table
    // when revenue returns before the bookings list.
    http.setJWT();
    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/revenue?${type === 'all' ? 'all=true' : ""}`, data)
    );

    if (success) {
      const { totalRevenue } = success.data.data;
      dispatch?.(bookingActions.setCompletedRevenue(totalRevenue))

    }

    return [success, error];
  },

  getBookingsRatings: async () => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/ratings`, { page: 1, page_size: 30 })
    );

    if (success) {
      const { trips } = success.data.data;
      dispatch?.(bookingActions.setBookingsRatings(trips));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  updateBillingAddress: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/updatebillingaddress/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;

      dispatch?.(modalActions.closeModal());
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  underReviewBooking: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/review/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },
  underReviewSkipBooking: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/review-skip/${id}`)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },
  rebateBooking: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/rebate/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },
  AddPaymentBooking: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/add-payment/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },
  editPaymentBooking: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/edit-payment/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },
  AddExtensionBooking: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/extend/${id}`, data)
    );

    if (success)
      BookingService.getBooking(id)


    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },



  sendToMarket: async (id: string, type: BookingType) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/sendtomarket/${id}`)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(modalActions.closeModal());
      dispatch?.(bookingActions.setBooking(trip));
      dispatch?.(bookingActions.sendMarket({ id, type, booking: trip }));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  acceptFromMarket: async (id: string, type: BookingType) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/acceptfrommarket/${id}`)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(modalActions.closeModal());
      dispatch?.(bookingActions.setBooking(trip));
      dispatch?.(bookingActions.acceptMarket({ id, type, booking: trip }));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  assignBooking: async (id: any, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/assign/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(modalActions.closeModal());
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  unassignBooking: async (id: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/unassign/${id}`)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(modalActions.closeModal());
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  cancelBookingPreview: async (id: string) => {
    http.setJWT();
    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/cancel-preview/${id}`, {})
    );
    if (success) {
      return [success.data?.data, null];
    }
    return [null, error];
  },

  cancelBooking: async (id: any, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/cancel/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(modalActions.closeModal());
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },
  requestCancellation: async (id: any, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    // No status change, but the booking is now frozen and hidden from the
    // operational tabs — so the loaded booking has to reflect that at once, or
    // the banner, the locked actions and the button label all stay stale until a
    // manual reload. Success/error toasts are handled by the http layer.
    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/cancel-request/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(modalActions.closeModal());
      dispatch?.(
        bookingActions.setCancellationRequest({
          pending: true,
          request: trip?.cancellation_request,
        })
      );
      // It has left Market/Planned/etc., so counts and any mounted list are stale.
      dispatch?.(bookingActions.refresh());
      BookingService.getTripTabs();
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },
  // Clear a pending cancellation request without cancelling the booking, so it
  // returns to the normal tabs and to the partner/driver market. Privileged
  // admins only; the requester is deliberately not notified.
  undoCancellationRequest: async (id: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/cancel-request/${id}/undo`, {})
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(modalActions.closeModal());
      dispatch?.(
        bookingActions.setCancellationRequest({
          pending: false,
          request: trip?.cancellation_request,
        })
      );
      // The row leaves the Cancellation Requests tab and the badge drops.
      dispatch?.(bookingActions.refresh());
      BookingService.getTripTabs();
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },
  // Privileged admins (booking-modification) a cancellation request can be sent
  // to — returned with names so the requester can pick recipients.
  getCancellationApprovers: async (): Promise<{ email: string; name: string }[]> => {
    http.setJWT();
    const [success]: any = await Promisable.asPromise(
      http.get(`${url}/cancellation-approvers`)
    );
    return success?.data?.data?.approvers || [];
  },
  refundBooking: async (id: any, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/refund/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(modalActions.closeModal());
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  updateBookingStatus: async (id: any, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/update-status/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(modalActions.closeModal());
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  addAdminNotes: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/note/${id}`, data)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
    }

    return [success, error];
  },
  generateInvoice: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(bookingActions.setLoader(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/generate-invoice/${id}`)
    );

    if (success) {
      const { trip } = success.data.data;
      dispatch?.(bookingActions.setBooking(trip));
    }

    dispatch?.(bookingActions.setLoader(false));
    return [success, error];
  },
  getSearchBookings: async (data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(bookingActions.setLoading({ type: "search", loading: true }));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/search`, data)
    );

    if (success) {
      const { bookings } = success.data.data;
      const count = bookings.length || 0
      dispatch?.(bookingActions.setSearchBookings({ type: "search", count, bookings }));
      dispatch?.(bookingActions.setSearchBookingsWithName({ type: "search", count, bookings }));
    }
    else dispatch?.(bookingActions.setBooking({ data: "Not Found" }));

    dispatch?.(bookingActions.setLoading({ type: "search", loading: false }));

    return [success, error];
  },

  getTripTabs: async () => {
    const dispatch = getAppDispatch();

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/trip-tabs`)
    );

    if (success) {
      const { bookings } = success.data.data;
      dispatch?.(bookingActions.setBookingCountTabs(bookings));

    }

    return [success, error];
  },

};

export default BookingService;
